'use client';

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from '@/i18n/routing';
import type { MarketQuoteGroup, MarketQuote, NewsItem } from '@/lib/api';
import BrokerCard from './BrokerCard';

type Tab = 'market' | 'news';

// 市場 / 新聞 two-tab view. Data comes from the server component (market page)
// so the 30s AutoRefresh keeps both tabs current; tab choice is client state
// and survives refreshes.
export default function MarketView({
  groups,
  news,
  asOf,
}: {
  groups: MarketQuoteGroup[];
  news: NewsItem[];
  asOf?: string;
}) {
  const [tab, setTab] = useState<Tab>('market');

  return (
    <div>
      {/* 分頁切換 */}
      <div className="mb-4 grid grid-cols-2 gap-1 rounded-2xl bg-zinc-100 p-1 dark:bg-zinc-800/70">
        {(
          [
            { key: 'market', label: '市場' },
            { key: 'news', label: '新聞' },
          ] as { key: Tab; label: string }[]
        ).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-xl py-2 text-sm font-semibold transition-all ${
              tab === key
                ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100'
                : 'text-zinc-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'market' ? (
        groups.length === 0 ? (
          <Empty text="暫時無法取得行情" />
        ) : (
          <div className="space-y-5">
            {asOf && (
              <p className="-mt-2 flex items-center gap-1.5 text-[11px] text-zinc-400">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                報價更新於 {fmtTime(asOf)} · 部分標的依來源有延遲
              </p>
            )}
            {groups.map((g) => (
              <section key={g.key}>
                <h2 className="mb-2 flex items-center gap-2 text-base font-bold">
                  <span className="h-4 w-1 rounded-full bg-gradient-to-b from-emerald-400 to-teal-500" />
                  {g.name}
                </h2>
                <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-sm shadow-zinc-200/50 dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-none">
                  {g.quotes.map((quote, i) => (
                    <QuoteRow key={quote.symbol} q={quote} last={i === g.quotes.length - 1} />
                  ))}
                </div>
              </section>
            ))}
            {/* 券商推薦 — 看完行情想交易 */}
            <BrokerCard />
          </div>
        )
      ) : news.length === 0 ? (
        <Empty text="今日新聞整理中" />
      ) : (
        <ul className="space-y-2">
          {news.map((item) => (
            <li key={item.id}>
              <a
                href={item.source_url}
                target="_blank"
                rel="noreferrer"
                className="flex gap-3 rounded-xl border border-zinc-200/80 bg-white p-3 shadow-sm shadow-zinc-200/40 transition active:scale-[0.99] dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-none"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-snug">{item.headline}</p>
                  {item.summary && (
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-500">
                      {item.summary}
                    </p>
                  )}
                  <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-brand">
                    {item.source_name} <ExternalLink size={11} />
                  </span>
                </div>
                {item.image_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    className="h-16 w-20 shrink-0 rounded-lg object-cover"
                  />
                ) : null}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <p className="rounded-2xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 dark:border-zinc-700">
      ⚠️ {text}
    </p>
  );
}

// 紅漲綠跌 quote row; rows with a detail page deep-link to /stocks/[symbol].
function QuoteRow({ q, last }: { q: MarketQuote; last: boolean }) {
  const up = q.change_pct >= 0;
  const body = (
    <div
      className={`flex items-center justify-between px-4 py-3 transition-colors active:bg-emerald-50/60 dark:active:bg-zinc-800 ${
        last ? '' : 'border-b border-zinc-100 dark:border-zinc-800'
      }`}
    >
      <div>
        <span className="text-sm font-medium">{q.name}</span>
        <span className="ml-2 text-xs text-zinc-400">{q.symbol}</span>
      </div>
      {q.ok ? (
        <div className="flex items-center gap-2 text-right tabular-nums">
          <span className="text-sm font-semibold tracking-tight">{fmtPrice(q.price)}</span>
          <span
            className={`inline-block min-w-[64px] rounded-lg px-1.5 py-1 text-center text-xs font-semibold ${
              up
                ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
            }`}
          >
            {up ? '+' : ''}
            {q.change_pct.toFixed(2)}%
          </span>
        </div>
      ) : (
        <span className="text-xs text-zinc-400">—</span>
      )}
    </div>
  );
  if (q.asset_class) {
    return (
      <Link
        href={`/stocks/${encodeURIComponent(q.symbol)}?asset_class=${q.asset_class}&name=${encodeURIComponent(q.name)}`}
        className="block"
      >
        {body}
      </Link>
    );
  }
  return body;
}

function fmtPrice(p: string): string {
  const n = Number(p);
  if (!Number.isFinite(n)) return p;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// Local HH:MM of the server's as_of timestamp — reassures users data is live.
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false });
}
